import axios from 'axios';

import { readAlphaDomainPool, readAlphaLoginSession } from '@shared/lib/general/alphaAuth';

const API_URL = process.env.API_URL || 'http://localhost:8080/userapp/api';
const ENVIRONMENT = process.env.CURRENT_ENV || 'develop';
const AUTH_URL =
  process.env.AUTH_URL || 'https://smacdemocog.auth.us-west-2.amazoncognito.com/oauth2';
const AUTH_CLIENT_ID = process.env.AUTH_CLIENT_ID || '';

const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 150000,
  params: {},
});

function getCurrentUser() {
  const user = localStorage.getItem('currentUser');
  return user ? JSON.parse(user) : null;
}

function setCurrentUser(user) {
  localStorage.setItem('currentUser', JSON.stringify(user));
}

function clearCurrentUser() {
  localStorage.removeItem('currentUser');
}

apiClient.interceptors.request.use((config) => {
  const newConfig = { ...config };
  const currentUser = getCurrentUser();

  if (currentUser) {
    const accessToken = currentUser.access_token || currentUser.token;
    if (accessToken) {
      newConfig.headers.Authorization = `Bearer ${accessToken}`;
    }
  }

  const alphaSession = readAlphaLoginSession();
  if (alphaSession) {
    newConfig.headers['Alpha-Login-Session'] =
      `${alphaSession.loginSessionId}:${alphaSession.loginSessionSecret}`;
  }

  newConfig.headers.Application = 'NOC';
  newConfig.headers.Platform = 'Web';
  newConfig.headers.Environment = ENVIRONMENT;

  return newConfig;
});

let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
}

apiClient.interceptors.response.use(
  (response) => {
    if (localStorage.getItem('maintenanceMode')) {
      localStorage.removeItem('maintenanceMode');
    }
    return response;
  },
  async (error) => {
    if (error.response?.status === 503) {
      const { data } = error.response;
      localStorage.setItem('maintenanceMode', JSON.stringify(data));
      window.location.href = '/not-found';
      return;
    }

    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            },
            reject,
          });
        });
      }

      isRefreshing = true;

      try {
        const currentUser = getCurrentUser();
        const refreshToken = currentUser?.refresh_token;

        if (!refreshToken) {
          window.location.href = '/login';
          return;
        }

        const pool = readAlphaDomainPool();
        const baseUrl = pool?.hostedDomain ? pool.hostedDomain.replace(/\/$/, '') : AUTH_URL;
        const clientId = pool?.appClientId || AUTH_CLIENT_ID;

        const formData = new URLSearchParams();
        formData.append('grant_type', 'refresh_token');
        formData.append('client_id', clientId);
        formData.append('refresh_token', refreshToken);

        const tokenResponse = await axios.post(`${baseUrl}/token`, formData.toString(), {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        });

        const {
          access_token: newAccessToken,
          refresh_token: newRefreshToken,
          id_token: newIdToken,
        } = tokenResponse.data;

        const updatedUser = {
          ...currentUser,
          access_token: newAccessToken,
          refresh_token: newRefreshToken || refreshToken,
          id_token: newIdToken,
        };

        setCurrentUser(updatedUser);
        processQueue(null, newAccessToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (err) {
        processQueue(err, null);
        clearCurrentUser();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
